import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { OATBIZ_HOST, OON_PROTEIN_COFFEE_URL } from '@/services/constants'
import { useToastStore } from '@/plugins/toast'

export const useReferralStore = defineStore('referral', () => {
  const toast = useToastStore()

  const code = ref<string>()
  const campaign = ref<string>()
  const referrer = ref<string>()
  const fingerprint = ref<string>()
  const isValidating = ref(false)
  const isInvalid = ref(false)
  const INVALID_MESSAGE = 'Sorry, that referral code is invalid.'

  const hasReferral = computed(() => code.value?.length ? true : false)

  const oonReferralUrl = computed(() => {
    if (!campaign.value?.length) {
      return OON_PROTEIN_COFFEE_URL
    }
    return `${OON_PROTEIN_COFFEE_URL}?utm_source=referral&utm_campaign=${campaign.value}`
  })

  const validateCode = async (inputCode: string): Promise<{ code: string, fingerprint?: string } | null> => {
    isValidating.value = true
    isInvalid.value = false
    try {
      const url = new URL(`${OATBIZ_HOST}/api/referrals/validate-code`)
      url.searchParams.set('code', inputCode)
      url.searchParams.set('referrer', referrer.value ?? '')
      const response = await fetch(url.toString())
      const data = await response.json()
      if (!data?.code) {
        throw new Error(data?.message || INVALID_MESSAGE)
      }
      return { code: data.code, fingerprint: data.fingerprint }
    } catch (error) {
      console.warn('[referral] Could not validate code', error)
      isInvalid.value = true
      return null
    } finally {
      isValidating.value = false
    }
  }

  const setReferralCode = async (inputCode: string) => {
    const validated = await validateCode(inputCode)
    if (validated) {
      code.value = validated.code
      fingerprint.value = validated.fingerprint
      toast.alert({ message: 'Referral Discount Applied!', type: 'notice', duration: 5000 })
    } else {
      reset()
    }
  }

  const init = () => {
    const urlParams = new URLSearchParams(window.location.search)
    const param = (name: string) => String(urlParams.get(name)).toUpperCase()
    const utmSource = param('utm_source')
    const utmCampaign = param('utm_campaign')

    if (utmSource === 'REFERRAL' && utmCampaign !== campaign.value) {
      campaign.value = utmCampaign
      referrer.value = document.referrer
    }

    if (campaign.value && !hasReferral.value) {
      setReferralCode(campaign.value)
    }
  }

  const reset = () => {
    code.value = campaign.value = fingerprint.value = referrer.value = undefined
  }

  return {
    code,
    campaign,
    fingerprint,
    isValidating,
    isInvalid,
    hasReferral,
    oonReferralUrl,
    init
  }
}, {
  persist: {
    storage: window.sessionStorage,
    pick: [ 'code', 'campaign', 'fingerprint', 'referrer' ],
    afterHydrate: ({ store }) => {
      store.init()
      store.$persist()
    }
  }
})

